import { Stack } from "@mui/material";
import { purple } from "@mui/material/colors";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { InsightsViolations } from "generated-graphql/graphql";
import { useMemo } from "react";
import {
  PriorityChartColors,
  ProgramAreaColors,
  generateQuarters,
  getPriorityChartColorByIndex,
} from "util/insights";
import { useOverviewState } from "../OverviewContext";

export const allowedViolationsBarChartTypes = [
  "byFacility",
  "byProgramArea",
  "byQuarter",
  "byQuarterProgramArea",
] as const;

export type ViolationsBarChartType =
  (typeof allowedViolationsBarChartTypes)[number];

export type ViolationsBarChartProps = {
  data: InsightsViolations | undefined;
  graphType: ViolationsBarChartType;
};

export const ViolationsBarChart = ({
  data,
  graphType,
}: ViolationsBarChartProps) => {
  if (!data) return null;

  switch (graphType) {
    case "byFacility":
      return <ByFacility data={data} />;
    case "byProgramArea":
      return <ByProgramArea data={data} />;
    case "byQuarter":
      return <ByQuarter data={data} />;
    case "byQuarterProgramArea":
      return <ByQuarterProgramArea data={data} />;
    default:
      return null;
  }
};

const ByFacility = ({
  data,
  limit = 5,
}: {
  data: InsightsViolations;
  limit?: number;
}) => {
  const { drillDownToEcho } = useOverviewState();
  const facilitiesWithMostViolations = useMemo(
    () =>
      data.violationsByFacility
        .filter((f) => f.totalViolations > 0) // Do not show facilities with 0 violations
        .slice() // Need to make a copy since this array is not mutable
        .sort((a, b) => {
          const violationDiff = b.totalViolations - a.totalViolations;
          if (violationDiff !== 0) return violationDiff;
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        })
        .slice(0, limit),
    [data.violationsByFacility, limit]
  );

  return (
    <Stack display="flex" direction="row" height="100%">
      <BarChart
        dataset={
          facilitiesWithMostViolations.length
            ? facilitiesWithMostViolations
            : [{ totalViolations: 0, name: "No data" }]
        }
        series={[
          {
            type: "bar",
            dataKey: "totalViolations",
            valueFormatter: (value) => `Violations (${value})`,
          },
        ]}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "name",
            categoryGapRatio: 0.4,
            colorMap: {
              type: "ordinal",
              colors: PriorityChartColors,
            },
          },
        ]}
        yAxis={[{ scaleType: "linear" }]}
        barLabel="value"
        barLabelColor="white"
        indexAxisLabelProperty={
          facilitiesWithMostViolations.length ? "name" : undefined
        }
        sx={{ flex: 1 }}
        onItemClick={(event, item) => {
          drillDownToEcho(() => ({
            registryId: facilitiesWithMostViolations[item.dataIndex].frsId,
          }));
        }}
      />
      <ChartLegend
        items={facilitiesWithMostViolations.map((d, index) => ({
          color: getPriorityChartColorByIndex(index),
          value: `${index + 1}. ${d.name}`,
        }))}
        sx={{ flex: 1, maxWidth: "35%" }}
      />
    </Stack>
  );
};

const ByProgramArea = ({ data }: { data: InsightsViolations }) => {
  const violationsByProgramArea = data.violationsByProgramArea;
  const { drillDownToEcho } = useOverviewState();
  return (
    <BarChart
      dataset={
        violationsByProgramArea.length
          ? violationsByProgramArea
          : [{ name: "No data", totalViolations: 0 }]
      }
      series={[
        {
          type: "bar",
          dataKey: "totalViolations",
          valueFormatter: (value) => `Violations (${value})`,
        },
      ]}
      xAxis={[
        {
          scaleType: "band",
          dataKey: "name",
          colorMap: {
            type: "ordinal",
            values: Object.keys(ProgramAreaColors),
            colors: Object.values(ProgramAreaColors),
          },
          categoryGapRatio: 0.65,
        },
      ]}
      yAxis={[{ scaleType: "linear" }]}
      barLabel="value"
      barLabelColor="white"
      onItemClick={(event, item) => {
        drillDownToEcho((f) => ({
          ...f,
          programAreaViolation: violationsByProgramArea[item.dataIndex].name,
        }));
      }}
    />
  );
};

const ByQuarter = ({ data }: { data: InsightsViolations }) => {
  const { drillDownToEcho } = useOverviewState();
  const quarters = generateQuarters().map((quarter) => {
    const total = (
      data.violationsByQuarter
        .find((q) => q.quarter === quarter)
        ?.violationsByProgramArea.map((pa) => pa.totalViolations) ?? []
    ).reduce((partialSum, a) => partialSum + a, 0);

    return {
      total,
      quarter,
    };
  });

  return (
    <BarChart
      dataset={quarters}
      series={[
        {
          type: "bar",
          dataKey: "total",
          color: purple[200],
          valueFormatter: (value) => `Violations (${value})`,
        },
      ]}
      xAxis={[
        {
          scaleType: "band",
          dataKey: "quarter",
          categoryGapRatio: 0.2,
          valueFormatter(value, context) {
            if (context.location === "tooltip") return value;
            if (context.location === "tick") {
              if (!value.includes("Q1")) {
                return value.split(" ")[0];
              }
            }
            return value.split(" ").join("\n");
          },
        },
      ]}
      yAxis={[{ scaleType: "linear" }]}
      onItemClick={() => {
        drillDownToEcho((f) => ({
          ...f,
          hasViolations: true,
        }));
      }}
    />
  );
};

const ByQuarterProgramArea = ({ data }: { data: InsightsViolations }) => {
  const { drillDownToEcho } = useOverviewState();
  const violationsByQuarter = data.violationsByQuarter;
  const programAreas = data.programAreas;

  const allQuarters = generateQuarters();

  const quarters = allQuarters.map((quarter) => {
    const quarterData = violationsByQuarter.find(
      (q) => q.quarter === quarter
    ) || { violationsByProgramArea: [] };

    const violations = Object.keys(ProgramAreaColors).reduce(
      (acc, programArea) => {
        const violation = quarterData.violationsByProgramArea.find(
          (v) => v.name === programArea
        );
        acc[programArea.toLowerCase()] = violation
          ? violation.totalViolations
          : 0;
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      ...violations,
      quarter,
    };
  });

  return (
    <>
      <ChartLegend
        config={{ direction: "row", position: "above" }}
        items={programAreas
          .slice()
          .sort()
          .map((pa) => ({
            color: ProgramAreaColors[pa],
            value: pa,
          }))}
        sx={{ paddingLeft: 6, position: "absolute" }}
      />
      <BarChart
        onItemClick={(event, barItemIdentifier) => {
          const clickedQuarter = quarters[barItemIdentifier.dataIndex];
          if (clickedQuarter) {
            drillDownToEcho((f) => ({
              ...f,
              programAreaViolation:
                barItemIdentifier.seriesId !== "Multiple Programs"
                  ? barItemIdentifier.seriesId
                  : undefined,
            }));
          }
        }}
        dataset={quarters}
        series={Object.keys(ProgramAreaColors).map((programArea) => ({
          type: "bar",
          stack: "total",
          id: programArea,
          dataKey: programArea.toLowerCase(),
          color: ProgramAreaColors[programArea],
          valueFormatter: (value) =>
            programArea === "Multiple Programs"
              ? `Multi-program facilities (${value})`
              : `${programArea} facilities (${value})`,
        }))}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "quarter",
            categoryGapRatio: 0.2,
            valueFormatter(value, context) {
              if (context.location === "tooltip") return value;
              if (context.location === "tick") {
                if (!value.includes("Q1")) {
                  return value.split(" ")[0];
                }
              }
              return value.split(" ").join("\n");
            },
          },
        ]}
        yAxis={[{ scaleType: "linear" }]}
        stackedTooltipTotalFormatter={(total) => `Total violations: ${total}`}
      />
    </>
  );
};
